// src/components/utils/navbar/NavBar.tsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  LogOut,
  User,
  DollarSign,
  Settings,
  CheckCircle,
  ShoppingCart,
} from "lucide-react";

interface NavBarProps {
  toggleSidebar: () => void;
  toggleProfileMenu: () => void;
  toggleNotifications: () => void;
  profileMenuOpen: boolean;
  notificationsOpen: boolean;
  usuario: string;
  rol: string;
  orgName?: string;
  handleLogout: () => void;
  showToggleMenu?: boolean;
  showNotificationsIcon?: boolean;
  sidebarOpen?: boolean;
}

const NavBar: React.FC<NavBarProps> = ({
  toggleSidebar,
  toggleProfileMenu,
  profileMenuOpen,
  usuario,
  rol,
  orgName,
  handleLogout,
  showToggleMenu = true,
  sidebarOpen,
}) => {
  const location = useLocation();
  const isCreatingOrganization = location.pathname === "/crear-organizacion";
  const isInCajaVenta = location.pathname === "/caja-venta";
  const isInSubscription = location.pathname === "/suscripcion";
  const [clicked, setClicked] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    setClicked(true);
    setTimeout(() => setClicked(false), 700);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        toggleProfileMenu();
      }
    };

    if (profileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileMenuOpen, toggleProfileMenu]);

  return (
    <nav className="w-full flex flex-wrap sm:flex-nowrap justify-between items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-primary-foreground shadow z-50 sticky top-0 h-auto min-h-[56px]">
      <div className="flex items-center gap-3">
      <button
        onClick={toggleSidebar}
        className="p-2 rounded-md text-primary-foreground hover:opacity-80 focus:outline-none"
      >
        <Menu className="h-6 w-6" />
      </button>
        <img
          src="/logos/logol.png"
          alt="Logo"
          className="h-10 w-auto object-contain hidden sm:block"
          draggable={false}
        />
      </div>

      <div className="flex items-center gap-4">
        {!isCreatingOrganization && !isInSubscription && (
          <Link
            to="/caja-venta"
            onClick={() => {
              handleClick();
              if (sidebarOpen) toggleSidebar(); // Solo si está abierto
            }}
            className={`flex items-center px-4 py-1 rounded-full font-semibold text-white transition-transform duration-150 ${
              isInCajaVenta ? "bg-green-600" : "bg-blue-600 hover:bg-blue-700"
            } ${clicked ? "scale-95" : ""}`}
          >
            {isInCajaVenta ? (
              <CheckCircle className="mr-2 h-4 w-4" />
            ) : (
              <ShoppingCart className="mr-2 h-4 w-4" />
            )}
            {isInCajaVenta ? "¡Hora de Vender!" : "Comenzar a Vender"}
          </Link>
        )}

        {isInSubscription && (
          <Link
            to="/"
            onClick={handleClick}
            className="flex items-center px-4 py-1 rounded-full font-semibold text-white bg-green-600 hover:bg-green-700"
          >
            <CheckCircle className="mr-2 h-4 w-4" />
            Volver al Inicio
          </Link>
        )}

        <div
          className="relative flex items-center gap-2 cursor-pointer"
          onClick={toggleProfileMenu}
        >
          <img
            src="/perfil/next5.png"
            alt="Perfil"
            className="h-10 w-10 rounded-full border-2 border-blue-500 object-cover"
          />
          <div className="text-sm hidden sm:flex flex-col leading-tight">
            <strong>{usuario}</strong>
            <span className="text-xs">{orgName}</span>
          </div>

          {profileMenuOpen && (
  <div
    ref={profileMenuRef}
    className="absolute top-full right-0 mt-4 w-64 rounded-2xl shadow-xl bg-card text-card-foreground ring-1 ring-border p-4 z-50 fade-in"
  >
    <div className="mb-3 border-b pb-3 text-sm">
      <div className="flex items-center gap-3">
        <img
          src="/perfil/next5.png"
          alt="Avatar"
          className="h-10 w-10 rounded-full border object-cover"
        />
        <div>
          <strong className="block">{usuario}</strong>
          <span className="text-xs text-muted-foreground">{orgName}</span>
        </div>
      </div>
      <div className="mt-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 w-fit">
        <CheckCircle className="h-4 w-4" />
        Activo
      </div>
    </div>

    <div className="space-y-2 text-sm">
      {isCreatingOrganization || isInSubscription ? (
        <Link
          to="/login"
          onClick={handleLogout}
          className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors"
        >
          <LogOut className="h-4 w-4" /> Cerrar Sesión
        </Link>
      ) : (
        <>
          {rol !== "vendedor" && (
            <>
              <Link
                to="/suscripcion"
                onClick={toggleProfileMenu}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors"
              >
                <DollarSign className="h-4 w-4" /> Suscripción
              </Link>
              <Link
                to="/configuracion"
                onClick={toggleProfileMenu}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors"
              >
                <Settings className="h-4 w-4" /> Configuración
              </Link>
            </>
          )}
          <Link
            to="/perfil"
            onClick={toggleProfileMenu}
            className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors"
          >
            <User className="h-4 w-4" /> Mi Perfil
          </Link>
          <Link
            to="/login"
            onClick={handleLogout}
            className="flex items-center gap-2 p-2 rounded-md hover:bg-red-100 text-red-600 transition-colors"
          >
            <LogOut className="h-4 w-4" /> Cerrar Sesión
          </Link>
        </>
      )}
    </div>
  </div>
)}
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
